import { Routes } from '@angular/router';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { LoginLayoutComponent } from './layouts/login-layout/login-layout.component';
import { PermissionGuard } from './core/guards/permission.guard';
import { AuthGuard } from './core/guards/session.guard';
import { PERMISSIONS } from './core/constants/permissions.constants';
import { NoAccessComponent } from './shared/components/no-access/no-access.component';
import { APP_ROUTES } from './core/constants/app-route.constants';
import { TempAuthGuard } from './core/guards/temporal-change-password-token.guard';

export const routes: Routes = [
  {
    path: 'auth',
    component: LoginLayoutComponent,
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/login/login.component').then(
            (m) => m.LoginComponent,
          ),
      },
    ],
  },

  {
    path: 'auth/change-temp-password',
    loadComponent: () =>
      import(
        './features/auth/login/change-temporary-password/change-temporary-password.component'
      ).then((m) => m.ChangeTempPasswordPage),
    canActivate: [TempAuthGuard],
  },

  {
    path: 'auth/forgot-password',
    loadComponent: () =>
      import(
        './features/auth/login/forgot-password/forgot-password.component'
      ).then((m) => m.ForgotPasswordComponent),
  },

  {
    path: 'app',
    component: MainLayoutComponent,
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import('./features/home/home.component').then((m) => m.HomeComponent),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.DASHBOARD,
            action: PERMISSIONS.ACTIONS.VIEW,
          },
        },
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/users/users.component').then(
            (m) => m.UsersComponent,
          ),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.USERS,
            action: PERMISSIONS.ACTIONS.VIEW,
          },
        },
      },
      {
        path: 'clients',
        loadComponent: () =>
          import('./features/clients/clients.component').then(
            (m) => m.ClientsComponent,
          ),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.CLIENTS,
            action: PERMISSIONS.ACTIONS.VIEW,
          },
        },
      },
      {
        path: 'tandas',
        loadComponent: () =>
          import('./features/tandas/tandas.component').then(
            (m) => m.TandasComponent,
          ),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.TANDAS,
            action: PERMISSIONS.ACTIONS.VER_RED_TANDAS,
          },
        },
      },
      {
        path: 'administration-tandas',
        loadComponent: () =>
          import(
            './features/administration-tandas/administration-tandas.component'
          ).then((m) => m.AdministrationTandasComponent),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.TANDAS,
            action: PERMISSIONS.ACTIONS.ADMINISTRACION_TANDAS,
          },
        },
      },
      {
        path: 'payments',
        loadComponent: () =>
          import('./features/payments/payment-list.component').then(
            (m) => m.PaymentsListComponent,
          ),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.CUENTAS,
            action: PERMISSIONS.ACTIONS.VER_SOLICITUDES_CUENTAS,
          },
        },
      },
      {
        path: 'payment-account-transfer',
        loadComponent: () =>
          import(
            './features/payment-account-transfer/payment-account-transfer.component'
          ).then((m) => m.PaymentAccountTransferComponent),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.PAYMENTS,
            action: PERMISSIONS.ACTIONS.VER_PAGOS_NIVEL2,
          },
        },
      },
      {
        path: 'payment-verification',
        loadComponent: () =>
          import(
            './features/payment-verification/payment-verification.component'
          ).then((m) => m.PaymentVerificationComponent),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.PAYMENTS,
            action: PERMISSIONS.ACTIONS.VER_PAGOS_NIVEL1,
          },
        },
      },
      {
        path: 'accounts',
        loadComponent: () =>
          import('./features/accounts/accounts.component').then(
            (m) => m.AccountsComponent,
          ),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.CUENTAS,
            action: PERMISSIONS.ACTIONS.VER_CUENTAS,
          },
        },
      },
      {
        path: 'remove-accounts',
        loadComponent: () =>
          import('./features/remove-account/remove-account.component').then(
            (m) => m.RemoveAccountComponent,
          ),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.CUENTAS,
            action: PERMISSIONS.ACTIONS.REMOVER_CUENTAS,
          },
        },
      },
      {
        path: 'bank-accounts',
        loadComponent: () =>
          import('./features/bank-accounts/bank-accounts.component').then(
            (m) => m.BankAccountsComponent,
          ),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.BANK_ACCOUNTS,
            action: PERMISSIONS.ACTIONS.VIEW,
          },
        },
      },
      {
        path: 'accounts-removed',
        loadComponent: () =>
          import('./features/accounts-removed/accounts-removed.component').then(
            (m) => m.AccountsRemovedComponent,
          ),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.CUENTAS,
            action: PERMISSIONS.ACTIONS.CUENTAS_REMOVIDAS,
          },
        },
      },
      {
        path: 'configuration',
        loadComponent: () =>
          import(
            './features/configuration/configuration/configuration.component'
          ).then((m) => m.ConfigurationComponent),
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          requiredAccess: {
            module: PERMISSIONS.MODULES.CONFIGURATION,
            actions: [
              PERMISSIONS.ACTIONS.VER_TERMINOS_CONDICIONES,
              PERMISSIONS.ACTIONS.VER_CONFIGURACION,
            ],
          },
        },
      },
      { path: '', redirectTo: 'home', pathMatch: 'full' },
    ],
  },
  {
    path: APP_ROUTES.NO_ACCESS,
    component: NoAccessComponent,
    canActivate: [AuthGuard],
  },

  { path: '**', redirectTo: APP_ROUTES.AUTH.LOGIN },
];
